import type { ReactNode } from 'react';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { Can } from '@/components/auth/Can';
import { PERM } from '@/constants/permissions';

interface ReportFrameProps {
  title: string;
  description: string;
  hasRestaurant: boolean;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isEmpty: boolean;
  onExportCsv?: () => void;
  onExportPdf?: () => void;
  exportDisabled?: boolean;
  emptyHint?: string;
  children: ReactNode;
}

/**
 * Standard chrome for a report view: title + description, optional CSV/PDF
 * export buttons (gated by report.export), and the shared
 * no-restaurant / loading / error / empty / content states.
 */
export function ReportFrame({
  title,
  description,
  hasRestaurant,
  isLoading,
  isError,
  onRetry,
  isEmpty,
  onExportCsv,
  onExportPdf,
  exportDisabled,
  emptyHint,
  children,
}: ReportFrameProps) {
  const showExport = !!(onExportCsv || onExportPdf);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        {showExport && (
          <Can name={PERM.REPORT_EXPORT}>
            <div className="flex gap-2">
              {onExportCsv && (
                <button onClick={onExportCsv} className="btn btn-outline flex items-center gap-2" disabled={exportDisabled}>
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
              )}
              {onExportPdf && (
                <button onClick={onExportPdf} className="btn btn-outline flex items-center gap-2" disabled={exportDisabled}>
                  <Download className="h-4 w-4" />
                  Export PDF
                </button>
              )}
            </div>
          </Can>
        )}
      </div>

      {!hasRestaurant ? (
        <div className="card"><div className="card-body text-center text-gray-500">Select a restaurant to view this report</div></div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : isError ? (
        <div className="card"><div className="card-body p-8 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
          <p className="text-gray-900 font-medium mb-3">Failed to load this report</p>
          {onRetry && (
            <button onClick={onRetry} className="btn btn-outline h-10">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          )}
        </div></div>
      ) : isEmpty ? (
        <div className="card"><div className="card-body text-center text-gray-500">{emptyHint ?? 'No data in range'}</div></div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Reusable simple data table. First column left-aligned, rest right-aligned.
 * Pass `footer` to render a bold totals row in a sticky-styled `<tfoot>`.
 */
export function SimpleTable({
  headers,
  rows,
  footer,
}: {
  headers: string[];
  rows: ReactNode[][];
  footer?: ReactNode[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td
                  key={ci}
                  className={`px-6 py-3 whitespace-nowrap text-sm ${ci === 0 ? 'text-left font-medium text-gray-900' : 'text-right text-gray-600'}`}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-900">
            <tr>
              {footer.map((c, ci) => (
                <td key={ci} className={`px-6 py-3 whitespace-nowrap text-sm ${ci === 0 ? 'text-left' : 'text-right'}`}>
                  {c}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
